import { useState, useEffect, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plane, Search, ArrowRight, Hotel, MapPin, ChevronDown, ChevronRight } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { TravelDeskChat } from '@/components/TravelDeskChat'
import { usePerformanceScope } from '@/lib/use-performance-scope'

/* ── Types ── */

interface Tour {
  id: string
  name: string
  status: string
  legCount: number
  memberCount: number
}

interface TourLeg {
  id: string
  name: string
  shows: Array<{ id: string; showId: string; title: string; date: string; city: string }>
}

interface ShowOption {
  id: string
  title: string
  date: string
  city: string
}

interface TransportOption {
  id: string
  partyGroup: string
  mode: string
  provider: string | null
  fromCity: string
  toCity: string
  totalCostMinor: number
  currency: string
  journeyMinutes: number
  departureAt: string | null
  selected: boolean
  bookedAt: string | null
  bookingRef: string | null
  notes: string | null
}

type PlanningLevel = 'tour' | 'leg' | 'show'

interface HopData {
  label: string
  city: string
  date: string | null
  venue: string | null
  showId: string | null
  isOrigin: boolean
  isReturn: boolean
}

/* ── Main Page ── */

export function TravelPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [planningLevel, setPlanningLevel] = useState<PlanningLevel>('show')
  const [selectedTourId, setSelectedTourId] = useState('')
  const [selectedLegId, setSelectedLegId] = useState('')
  const [selectedShowId, setSelectedShowId] = useState('')
  const [originAirport, setOriginAirport] = useState('LHR')
  const [passengers, setPassengers] = useState(1)
  const [searchingHops, setSearchingHops] = useState<Record<string, boolean>>({})
  const [expandedHops, setExpandedHops] = useState<Record<string, boolean>>({})
  const [searchingAll, setSearchingAll] = useState(false)

  useEffect(() => {
    if (!scope.active) return
    if (scope.kind === 'tour') {
      setPlanningLevel('tour')
      setSelectedTourId(scope.id ?? '')
      setSelectedLegId('')
      setSelectedShowId('')
    } else if (scope.kind === 'show') {
      setPlanningLevel('show')
      setSelectedShowId(scope.id ?? '')
    }
  }, [scope.active, scope.kind, scope.id])

  // Queries
  const tours = useQuery<Tour[], ApiClientError>({
    queryKey: ['tours'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
    enabled: planningLevel === 'tour' || planningLevel === 'leg',
  })

  const legs = useQuery<TourLeg[], ApiClientError>({
    queryKey: ['tour-legs', selectedTourId],
    queryFn: () => artistFetch<TourLeg[]>(`/tours/${selectedTourId}/legs`),
    enabled: !!selectedTourId,
  })

  const shows = useQuery<ShowOption[], ApiClientError>({
    queryKey: ['travel-shows'],
    queryFn: () => artistFetch<ShowOption[]>('/shows'),
    enabled: planningLevel === 'show',
  })

  // Build hops from selection
  function buildHops(): HopData[] {
    const hops: HopData[] = []
    const originName = AIRPORTS.find((a) => a.code === originAirport)?.name ?? originAirport

    if (planningLevel === 'tour' && selectedTourId && legs.data) {
      hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: true, isReturn: false })
      for (const leg of legs.data) {
        for (const show of leg.shows) {
          hops.push({ label: show.city, city: show.city, date: show.date, venue: show.title, showId: show.showId, isOrigin: false, isReturn: false })
        }
      }
      hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: false, isReturn: true })
    } else if (planningLevel === 'leg' && selectedLegId && legs.data) {
      const leg = legs.data.find((l) => l.id === selectedLegId)
      if (leg) {
        hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: true, isReturn: false })
        for (const show of leg.shows) {
          hops.push({ label: show.city, city: show.city, date: show.date, venue: show.title, showId: show.showId, isOrigin: false, isReturn: false })
        }
        hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: false, isReturn: true })
      }
    } else if (planningLevel === 'show' && selectedShowId && shows.data) {
      const show = shows.data.find((s) => s.id === selectedShowId)
      if (show) {
        hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: true, isReturn: false })
        hops.push({ label: show.city, city: show.city, date: show.date, venue: show.title, showId: show.id, isOrigin: false, isReturn: false })
        hops.push({ label: originName, city: originAirport, date: null, venue: null, showId: null, isOrigin: false, isReturn: true })
      }
    }

    return hops
  }

  const hops = buildHops()

  // For each hop transition (i -> i+1), the "flight" goes from hops[i].city to hops[i+1].city
  // The showId to use is hops[i+1].showId (destination show)
  function getHopTransitions(): Array<{ fromCity: string; toCity: string; showId: string | null; hopIndex: number; label: string; date: string | null; venue: string | null }> {
    const transitions: Array<{ fromCity: string; toCity: string; showId: string | null; hopIndex: number; label: string; date: string | null; venue: string | null }> = []
    for (let i = 0; i < hops.length - 1; i++) {
      const from = hops[i]!
      const to = hops[i + 1]!
      transitions.push({
        fromCity: from.city,
        toCity: to.city,
        showId: to.showId,
        hopIndex: i,
        label: to.label,
        date: to.date,
        venue: to.venue,
      })
    }
    return transitions
  }

  const transitions = getHopTransitions()

  async function searchHop(showId: string, fromCity: string) {
    setSearchingHops((prev) => ({ ...prev, [showId]: true }))
    try {
      await artistFetch(`/travel/shows/${showId}/search`, {
        method: 'POST',
        body: { fromCity, passengers },
      })
      qc.invalidateQueries({ queryKey: ['travel-options', showId] })
      setExpandedHops((prev) => ({ ...prev, [showId]: true }))
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Search failed')
    } finally {
      setSearchingHops((prev) => ({ ...prev, [showId]: false }))
    }
  }

  async function searchAllHopsSequential() {
    setSearchingAll(true)
    for (const t of transitions) {
      if (t.showId) {
        await searchHop(t.showId, t.fromCity)
      }
    }
    setSearchingAll(false)
  }

  async function searchLegItinerary(legId: string) {
    setSearchingAll(true)
    try {
      const result = await artistFetch<{
        legId: string
        hops: Array<{
          from: string
          to: string
          showId: string | null
          optionCount: number
          cheapestPence: number | null
          error: string | null
        }>
      }>(`/travel/legs/${legId}/search-itinerary`, {
        method: 'POST',
        body: { origin: originAirport, passengers },
      })
      // Invalidate all show option queries to refresh results
      for (const hop of result.hops) {
        if (hop.showId) {
          qc.invalidateQueries({ queryKey: ['travel-options', hop.showId] })
          setExpandedHops((prev) => ({ ...prev, [hop.showId!]: true }))
        }
      }
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Itinerary search failed')
    } finally {
      setSearchingAll(false)
    }
  }

  async function searchTourItinerary(tourId: string) {
    setSearchingAll(true)
    try {
      const result = await artistFetch<{
        tourId: string
        legs: Array<{
          legId: string
          legName: string
          hops: Array<{
            from: string
            to: string
            showId: string | null
            optionCount: number
            cheapestPence: number | null
            error: string | null
          }>
        }>
      }>(`/travel/tours/${tourId}/search-itinerary`, {
        method: 'POST',
        body: { origin: originAirport, passengers },
      })
      // Invalidate all show option queries to refresh results
      for (const leg of result.legs) {
        for (const hop of leg.hops) {
          if (hop.showId) {
            qc.invalidateQueries({ queryKey: ['travel-options', hop.showId] })
            setExpandedHops((prev) => ({ ...prev, [hop.showId!]: true }))
          }
        }
      }
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Itinerary search failed')
    } finally {
      setSearchingAll(false)
    }
  }

  function searchAllHops() {
    if (planningLevel === 'leg' && selectedLegId) {
      searchLegItinerary(selectedLegId)
    } else if (planningLevel === 'tour' && selectedTourId) {
      searchTourItinerary(selectedTourId)
    } else {
      searchAllHopsSequential()
    }
  }

  function toggleHopExpanded(showId: string) {
    setExpandedHops((prev) => ({ ...prev, [showId]: !prev[showId] }))
  }

  return (
    <div>
      <PageHeader
        title="Travel"
        subtitle="Plan flights across tours, legs, or individual shows. Compare options by total party cost."
      />
      <PerformanceScopeBanner />

      {/* Itinerary Selector */}
      <div className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-4">
        <div className="mb-4">
          <label className="mb-2 block text-xs uppercase tracking-wider text-ovation-muted">
            Planning level
          </label>
          <div className="flex gap-4">
            {(['tour', 'leg', 'show'] as const).map((level) => (
              <label key={level} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="planningLevel"
                  value={level}
                  checked={planningLevel === level}
                  onChange={() => {
                    setPlanningLevel(level)
                    setSelectedTourId('')
                    setSelectedLegId('')
                    setSelectedShowId('')
                    setExpandedHops({})
                  }}
                  className="accent-ovation-ink"
                />
                <span className="capitalize font-medium">{level}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* Tour selector (for Tour and Leg modes) */}
          {(planningLevel === 'tour' || planningLevel === 'leg') && (
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                Tour
              </label>
              <select
                value={selectedTourId}
                onChange={(e) => {
                  setSelectedTourId(e.target.value)
                  setSelectedLegId('')
                  setExpandedHops({})
                }}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              >
                <option value="">-- select tour --</option>
                {(tours.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.legCount} legs)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Leg selector (for Leg mode) */}
          {planningLevel === 'leg' && selectedTourId && (
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                Leg
              </label>
              <select
                value={selectedLegId}
                onChange={(e) => {
                  setSelectedLegId(e.target.value)
                  setExpandedHops({})
                }}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              >
                <option value="">-- select leg --</option>
                {(legs.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.shows.length} shows)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show selector (for Show mode) */}
          {planningLevel === 'show' && (
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                Show
              </label>
              <select
                value={selectedShowId}
                onChange={(e) => {
                  setSelectedShowId(e.target.value)
                  setExpandedHops({})
                }}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              >
                <option value="">-- select show --</option>
                {(shows.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {new Date(s.date).toLocaleDateString()} — {s.title} ({s.city})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Origin Airport */}
          <div className="min-w-[220px]">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
              Origin airport
            </label>
            <AirportPicker value={originAirport} onChange={setOriginAirport} />
          </div>

          {/* Passengers */}
          <div className="w-24">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
              Passengers
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={passengers}
              onChange={(e) => setPassengers(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Journey View */}
      {hops.length > 0 && (
        <div className="mb-6">
          {/* Hop cards row */}
          <div className="mb-4 flex items-start gap-2 overflow-x-auto pb-2">
            {hops.map((hop, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <div className="flex min-w-[140px] flex-col rounded-lg border border-ovation-ink/10 bg-white p-3">
                  <div className="mb-1 flex items-center gap-1.5">
                    {hop.isOrigin || hop.isReturn ? (
                      <MapPin className="h-3.5 w-3.5 text-ovation-muted" />
                    ) : (
                      <Plane className="h-3.5 w-3.5 text-ovation-muted" />
                    )}
                    <span className="text-sm font-medium truncate">{hop.label}</span>
                  </div>
                  {hop.isOrigin && (
                    <span className="text-xs text-ovation-muted">{originAirport}</span>
                  )}
                  {hop.isReturn && (
                    <span className="text-xs text-ovation-muted">Return</span>
                  )}
                  {hop.date && (
                    <span className="text-xs text-ovation-muted">
                      {new Date(hop.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {hop.venue && (
                    <span className="text-xs text-ovation-muted truncate">{hop.venue}</span>
                  )}
                </div>
                {idx < hops.length - 1 && (
                  <ArrowRight className="mt-4 h-4 w-4 flex-shrink-0 text-ovation-muted" />
                )}
              </div>
            ))}
          </div>

          {/* Search all button */}
          {transitions.length > 1 && (
            <div className="mb-4">
              <button
                type="button"
                onClick={searchAllHops}
                disabled={searchingAll || transitions.every((t) => !t.showId)}
                className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {searchingAll ? `Searching ${transitions.length} hops...` : 'Search entire itinerary'}
              </button>
            </div>
          )}

          {/* Individual hop transitions */}
          <div className="space-y-3">
            {transitions.map((t, idx) => (
              <HopTransitionCard
                key={`${t.fromCity}-${t.toCity}-${idx}`}
                fromCity={t.fromCity}
                toCity={t.toCity}
                showId={t.showId}
                label={t.label}
                date={t.date}
                venue={t.venue}
                isReturn={idx === transitions.length - 1}
                searching={t.showId ? !!searchingHops[t.showId] : false}
                expanded={t.showId ? !!expandedHops[t.showId] : false}
                onSearch={() => t.showId && searchHop(t.showId, t.fromCity)}
                onToggleExpand={() => t.showId && toggleHopExpanded(t.showId)}
                passengers={passengers}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {hops.length === 0 && (
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-8 text-center text-sm text-ovation-muted">
          <Plane className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p>Select a {planningLevel} above to build your travel itinerary.</p>
        </div>
      )}

      <div className="mt-8">
        <TravelDeskChat mode="artist" />
      </div>
    </div>
  )
}

/* ── Hop Transition Card ── */

function HopTransitionCard({
  fromCity,
  toCity,
  showId,
  label,
  date,
  venue,
  isReturn,
  searching,
  expanded,
  onSearch,
  onToggleExpand,
  passengers,
}: {
  fromCity: string
  toCity: string
  showId: string | null
  label: string
  date: string | null
  venue: string | null
  isReturn: boolean
  searching: boolean
  expanded: boolean
  onSearch: () => void
  onToggleExpand: () => void
  passengers: number
}) {
  const [hotelMessage, setHotelMessage] = useState(false)

  const options = useQuery<TransportOption[], ApiClientError>({
    queryKey: ['travel-options', showId],
    queryFn: () => artistFetch<TransportOption[]>(`/travel/shows/${showId}/options`),
    enabled: !!showId,
  })

  const optionCount = options.data?.length ?? 0
  const cheapest = options.data?.length
    ? Math.min(...options.data.map((o) => o.totalCostMinor))
    : null

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{fromCity}</span>
          <ArrowRight className="h-3.5 w-3.5 text-ovation-muted" />
          <span className="font-medium">{label}</span>
          {isReturn && <span className="text-xs text-ovation-muted">(return)</span>}
        </div>

        {date && (
          <span className="text-xs text-ovation-muted">
            {new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        )}
        {venue && (
          <span className="text-xs text-ovation-muted">| {venue}</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {optionCount > 0 && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs hover:bg-ovation-ink/5"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {optionCount} option{optionCount !== 1 ? 's' : ''} from {cheapest !== null ? `\u00A3${(cheapest / 100).toLocaleString()}` : '...'}
            </button>
          )}

          {showId && (
            <button
              type="button"
              onClick={onSearch}
              disabled={searching}
              className="flex items-center gap-1.5 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <Plane className="h-3 w-3" />
              {searching ? 'Searching...' : 'Search flights'}
            </button>
          )}

          {!isReturn && (
            <button
              type="button"
              onClick={() => setHotelMessage(true)}
              className="flex items-center gap-1.5 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs hover:bg-ovation-ink/5"
            >
              <Hotel className="h-3 w-3" />
              Hotels
            </button>
          )}
        </div>
      </div>

      {hotelMessage && (
        <div className="mt-3 rounded-md border border-ovation-ink/10 bg-ovation-ink/5 px-3 py-2 text-xs text-ovation-muted">
          Hotel search coming soon
        </div>
      )}

      {expanded && showId && (
        <div className="mt-4">
          <AsyncBoundary isLoading={options.isLoading} error={options.error}>
            {(options.data ?? []).length === 0 ? (
              <div className="text-xs text-ovation-muted">No options found. Try searching again.</div>
            ) : (
              <DataTable
                rows={options.data ?? []}
                rowKey={(r) => r.id}
                columns={[
                  {
                    key: 'mode',
                    header: 'Mode',
                    render: (r) => (
                      <div className="flex items-center gap-2">
                        <Plane className="h-3 w-3" />
                        <span className="capitalize">{r.mode.replace(/_/g, ' ')}</span>
                      </div>
                    ),
                  },
                  { key: 'group', header: 'Group', render: (r) => r.partyGroup.replace(/_/g, ' ') },
                  { key: 'provider', header: 'Provider', render: (r) => r.provider ?? '--' },
                  { key: 'route', header: 'Route', render: (r) => `${r.fromCity} \u2192 ${r.toCity}` },
                  {
                    key: 'cost',
                    header: 'Total cost',
                    align: 'right',
                    render: (r) => `\u00A3${(r.totalCostMinor / 100).toLocaleString()}`,
                  },
                  {
                    key: 'duration',
                    header: 'Journey',
                    render: (r) => {
                      const h = Math.floor(r.journeyMinutes / 60)
                      const m = r.journeyMinutes % 60
                      return `${h}h ${m}m`
                    },
                  },
                  {
                    key: 'actions',
                    header: '',
                    align: 'right',
                    render: (r) =>
                      r.selected ? (
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                          Selected {r.bookedAt ? '\u2713 Booked' : ''}
                        </span>
                      ) : (
                        <SelectOptionButton optionId={r.id} showId={showId} />
                      ),
                  },
                ]}
              />
            )}
          </AsyncBoundary>
        </div>
      )}
    </div>
  )
}

/* ── Select Option Button ── */

function SelectOptionButton({ optionId, showId }: { optionId: string; showId: string }) {
  const qc = useQueryClient()

  async function selectOption() {
    try {
      await artistFetch(`/travel/options/${optionId}/select`, { method: 'POST' })
      qc.invalidateQueries({ queryKey: ['travel-options', showId] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <button
      type="button"
      onClick={selectOption}
      className="rounded-md border border-ovation-ink/15 px-3 py-1 text-xs hover:bg-ovation-ink/5"
    >
      Select
    </button>
  )
}

/* ── Airport picker with search ── */

const AIRPORTS = [
  // UK & Ireland
  { code: 'LHR', name: 'London Heathrow' },
  { code: 'LGW', name: 'London Gatwick' },
  { code: 'STN', name: 'London Stansted' },
  { code: 'LTN', name: 'London Luton' },
  { code: 'LCY', name: 'London City' },
  { code: 'MAN', name: 'Manchester' },
  { code: 'BHX', name: 'Birmingham' },
  { code: 'GLA', name: 'Glasgow' },
  { code: 'EDI', name: 'Edinburgh' },
  { code: 'LPL', name: 'Liverpool' },
  { code: 'NCL', name: 'Newcastle' },
  { code: 'BRS', name: 'Bristol' },
  { code: 'BFS', name: 'Belfast' },
  { code: 'CWL', name: 'Cardiff' },
  { code: 'LBA', name: 'Leeds Bradford' },
  { code: 'DUB', name: 'Dublin' },
  // Europe
  { code: 'CDG', name: 'Paris Charles de Gaulle' },
  { code: 'ORY', name: 'Paris Orly' },
  { code: 'AMS', name: 'Amsterdam Schiphol' },
  { code: 'BER', name: 'Berlin Brandenburg' },
  { code: 'MUC', name: 'Munich' },
  { code: 'FRA', name: 'Frankfurt' },
  { code: 'FCO', name: 'Rome Fiumicino' },
  { code: 'MXP', name: 'Milan Malpensa' },
  { code: 'MAD', name: 'Madrid Barajas' },
  { code: 'BCN', name: 'Barcelona El Prat' },
  { code: 'LIS', name: 'Lisbon' },
  { code: 'VIE', name: 'Vienna' },
  { code: 'ZRH', name: 'Zurich' },
  { code: 'GVA', name: 'Geneva' },
  { code: 'BRU', name: 'Brussels' },
  { code: 'CPH', name: 'Copenhagen' },
  { code: 'ARN', name: 'Stockholm Arlanda' },
  { code: 'OSL', name: 'Oslo Gardermoen' },
  { code: 'HEL', name: 'Helsinki' },
  { code: 'PRG', name: 'Prague' },
  { code: 'WAW', name: 'Warsaw' },
  { code: 'BUD', name: 'Budapest' },
  { code: 'ATH', name: 'Athens' },
  { code: 'IST', name: 'Istanbul' },
  // Americas
  { code: 'JFK', name: 'New York JFK' },
  { code: 'EWR', name: 'New York Newark' },
  { code: 'LGA', name: 'New York LaGuardia' },
  { code: 'LAX', name: 'Los Angeles' },
  { code: 'ORD', name: 'Chicago O\'Hare' },
  { code: 'MIA', name: 'Miami' },
  { code: 'SFO', name: 'San Francisco' },
  { code: 'BOS', name: 'Boston' },
  { code: 'ATL', name: 'Atlanta' },
  { code: 'DFW', name: 'Dallas Fort Worth' },
  { code: 'IAH', name: 'Houston' },
  { code: 'SEA', name: 'Seattle' },
  { code: 'DEN', name: 'Denver' },
  { code: 'LAS', name: 'Las Vegas' },
  { code: 'IAD', name: 'Washington Dulles' },
  { code: 'PHL', name: 'Philadelphia' },
  { code: 'DTW', name: 'Detroit' },
  { code: 'MSP', name: 'Minneapolis' },
  { code: 'PHX', name: 'Phoenix' },
  { code: 'MCO', name: 'Orlando' },
  { code: 'BNA', name: 'Nashville' },
  { code: 'SAN', name: 'San Diego' },
  { code: 'YYZ', name: 'Toronto Pearson' },
  { code: 'YVR', name: 'Vancouver' },
  { code: 'YUL', name: 'Montreal' },
  { code: 'MEX', name: 'Mexico City' },
  { code: 'GRU', name: 'Sao Paulo Guarulhos' },
  { code: 'EZE', name: 'Buenos Aires Ezeiza' },
  // Asia & Middle East
  { code: 'NRT', name: 'Tokyo Narita' },
  { code: 'HND', name: 'Tokyo Haneda' },
  { code: 'KIX', name: 'Osaka Kansai' },
  { code: 'ICN', name: 'Seoul Incheon' },
  { code: 'PEK', name: 'Beijing' },
  { code: 'PVG', name: 'Shanghai Pudong' },
  { code: 'HKG', name: 'Hong Kong' },
  { code: 'SIN', name: 'Singapore Changi' },
  { code: 'BKK', name: 'Bangkok Suvarnabhumi' },
  { code: 'BOM', name: 'Mumbai' },
  { code: 'DEL', name: 'Delhi' },
  { code: 'DXB', name: 'Dubai' },
  { code: 'AUH', name: 'Abu Dhabi' },
  { code: 'DOH', name: 'Doha' },
  { code: 'TLV', name: 'Tel Aviv' },
  { code: 'TPE', name: 'Taipei' },
  { code: 'MNL', name: 'Manila' },
  { code: 'CGK', name: 'Jakarta' },
  { code: 'KUL', name: 'Kuala Lumpur' },
  // Africa & Oceania
  { code: 'SYD', name: 'Sydney' },
  { code: 'MEL', name: 'Melbourne' },
  { code: 'BNE', name: 'Brisbane' },
  { code: 'AKL', name: 'Auckland' },
  { code: 'JNB', name: 'Johannesburg' },
  { code: 'CPT', name: 'Cape Town' },
  { code: 'NBO', name: 'Nairobi' },
  { code: 'CAI', name: 'Cairo' },
  { code: 'LOS', name: 'Lagos' },
  { code: 'CMN', name: 'Casablanca' },
] as const

function AirportPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const selected = AIRPORTS.find((a) => a.code === value)
  const query = search.toLowerCase()
  const filtered = query
    ? AIRPORTS.filter(
        (a) =>
          a.name.toLowerCase().includes(query) ||
          a.code.toLowerCase().includes(query),
      )
    : AIRPORTS

  return (
    <div className="relative">
      <input
        value={open ? search : selected ? `${selected.name} (${selected.code})` : value}
        onChange={(e) => {
          setSearch(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => {
          setOpen(true)
          setSearch('')
        }}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        placeholder="Search airport or city..."
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-ovation-ink/15 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-ovation-muted">
              No airports found. Type a 3-letter IATA code directly.
            </div>
          ) : (
            filtered.slice(0, 20).map((a) => (
              <button
                key={a.code}
                type="button"
                onClick={() => {
                  onChange(a.code)
                  setSearch('')
                  setOpen(false)
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-ovation-ink/5 ${
                  a.code === value ? 'bg-ovation-ink/10 font-medium' : ''
                }`}
              >
                {a.name} <span className="text-ovation-muted">({a.code})</span>
              </button>
            ))
          )}
        </div>
      )}
      {open && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  )
}
